import React from 'react';
import { Box, Flex, HStack } from '@chakra-ui/react';
import { SyncingInstallationAlert } from '#components';
import { useAccount } from '#contexts/account';
import { t } from '#i18n';
import {
  DateRangePicker,
  GranularitySelect,
  GroupBySelect,
  PerformanceLineChart,
  RepositorySelect,
} from './components';
import { useReviewerStatsTimeSeriesQuery } from './hooks/useReviewerStatsTimeSeriesQuery';
import { filterTopReviewers } from './utils/filterTopReviewers';
import { useFilters } from './contexts/filters';

const TOP_REVIEWERS = 7;
const FILTER_STAT = 'totalReviews';

export const OverTime = () => {
  const {
    filters,
    setDateRange,
    setGranularity,
    setGroupBy,
    setRepositoryIds,
  } = useFilters();
  const {
    dateRange,
    granularity,
    groupBy,
    repositoryIds,
  } = filters;

  const { currentInstallation, refetch: refetchInstallations } = useAccount();

  const { reviewerStatsTimeSeries, isLoading, refetch } = useReviewerStatsTimeSeriesQuery({
    granularity,
    repositoryIds: repositoryIds?.length ? repositoryIds : undefined,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    groupBy,
  });

  const {
    totalComments,
    totalReviews,
    medianTimeToReview,
  } = filterTopReviewers(
    reviewerStatsTimeSeries,
    { top: TOP_REVIEWERS, basedOn: FILTER_STAT },
  ) || {};

  const stats = [
    { title: t('stats.pullRequestReviews.totalReviews'), data: totalReviews, type: 'count' },
    {
      title: t('stats.pullRequestReviews.medianTimeToReview'),
      data: medianTimeToReview,
      type: 'time',
    },
    { title: t('stats.pullRequestReviews.totalComments'), data: totalComments, type: 'count' },
  ];

  return (
    <>
      <SyncingInstallationAlert
        margin="32px 0 0"
        variant="left-accent"
        installation={currentInstallation}
        refetchInstallation={refetchInstallations}
        onStatusActive={refetch}
      />
      <Flex
        width="full"
        marginTop={8}
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'flex-start', lg: 'center' }}
        justifyContent={{ lg: 'space-between' }}
        gap={4}
      >
        <GroupBySelect groupBy={filters.groupBy} onChange={setGroupBy} />
        <HStack spacing={4} wrap="wrap">
          <RepositorySelect repositoryIds={repositoryIds} onChange={setRepositoryIds} />
          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
          <GranularitySelect
            dateRange={dateRange}
            granularity={granularity}
            onChange={setGranularity}
          />
        </HStack>
      </Flex>
      <Box
        display="flex"
        flexDirection="column"
        rowGap={6}
        marginTop={4}
        paddingBottom={12}
      >
        {stats.map(({ title, data, type }) => (
          <Box
            key={title}
            display="flex"
            flexDirection="column"
            padding={4}
            minHeight={400}
            maxHeight={400}
            border="1px solid"
            bg="primary.400"
            borderColor="primary.300"
            borderRadius="8px"
          >
            <PerformanceLineChart
              title={title}
              data={data}
              dateRange={dateRange}
              dataType={type}
              granularity={granularity}
              isLoading={isLoading}
            />
          </Box>
        ))}
      </Box>
    </>
  );
};
